import '../styles/global.css';
import type { AppProps } from 'next/app';
import React, { ReactElement, useEffect } from 'react';
import { Layout } from '@components/layout';
import type { NextPage } from 'next';
import { SidebarContextProvider } from '@components/context/SidebarContext';
import { LeftsideContextProvider } from '@components/context/LeftsideNavContext';
import { GradebookContextProvider } from '@components/context/GradebookContext';
import { UniversalModal } from '@components/modals/UniversalModal';
import { UniversalModalProvider } from '@components/context/UniversalModalContext';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { Permissions, Roles, useUserPermissions } from '@hooks/user/useUserPermissions';
import { useRouter } from 'next/router';
import { CourseContextProvider } from '@components/context/CourseContext';
import { CourseUnitContextProvider } from '@components/layout/leftNav/menus/units/CourseUnitsMenu';
import { ScriptHeader } from '@components/scripts/ScriptHeaders';
import dynamic from 'next/dynamic';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { IsSsrMobileContext } from '@components/context/IsMobileContext';
import { config as fontAwesomeConfig } from '@fortawesome/fontawesome-svg-core';
import { ComponentUIDProvider } from '@components/context/ComponentUID';
fontAwesomeConfig.autoAddCss = false;

const ReactQueryDevToolComponent = dynamic(
    () => import('react-query/devtools').then((mod) => mod.ReactQueryDevtools),
    { ssr: false },
);

const LottieLoaderFullscreen = dynamic(
    () =>
        import('@components/loader/LottieLoaderFullscreen').then(
            (mod) => mod.LottieLoaderFullscreen,
        ),
    { ssr: false },
);

type NextPageWithLayout = NextPage & {
    getLayout?: (page: ReactElement) => any;
    wrapAuth?: (page: ReactElement) => ReactElement;
    requiredPermission?: Permissions;
    requiredRole?: Roles;
    useDefault?: boolean;
    useEmpty?: boolean;
};

type AppPropsWithLayout = AppProps<{ isSsrMobile: boolean }> & {
    Component: any;
    pageProps: any;
} & NextPageWithLayout;

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    const isDefault = Component.useDefault ?? false;
    const noBaseLayout = Component.useEmpty ?? false;
    const [queryClient] = React.useState(() => new QueryClient());
    if (isDefault) {
        const getLayout =
            Component.getLayoKut ||
            ((page) => (
                <QueryClientProvider client={queryClient}>
                    <Hydrate state={pageProps.dehydratedState}>
                        <ClientAuth
                            requiredPermission={Component.requiredPermission}
                            requiredRole={Component.requiredRole}
                        >
                            <ScriptHeader />
                            <SidebarContextProvider>
                                <LeftsideContextProvider>
                                    <CourseContextProvider>
                                        <UniversalModalProvider>
                                            <CourseUnitContextProvider>
                                                <GradebookContextProvider>
                                                    <Layout
                                                        showHeader={!noBaseLayout}
                                                        showSidebar={!noBaseLayout}
                                                    >
                                                        <div className="w-full h-full">{page}</div>
                                                        <ReactQueryDevToolComponent
                                                            initialIsOpen
                                                            position="bottom-right"
                                                        />
                                                        <UniversalModal />
                                                    </Layout>
                                                </GradebookContextProvider>
                                            </CourseUnitContextProvider>
                                        </UniversalModalProvider>
                                    </CourseContextProvider>
                                </LeftsideContextProvider>
                            </SidebarContextProvider>
                        </ClientAuth>
                    </Hydrate>
                </QueryClientProvider>
            ));

        return (
            <ComponentUIDProvider>{getLayout(<Component {...pageProps} />)}</ComponentUIDProvider>
        );
    }

    return (
        <React.Fragment>
            {/* <GoogleAnalytics /> */}
            <ComponentUIDProvider>
                <QueryClientProvider client={queryClient}>
                    <ScriptHeader />
                    <UniversalModalProvider>
                        <Hydrate state={pageProps.dehydratedState}>
                            <IsSsrMobileContext.Provider value={pageProps.isSsrMobile}>
                                <div className="w-full h-full">
                                    <Component {...pageProps} />
                                </div>
                            </IsSsrMobileContext.Provider>
                            <ReactQueryDevToolComponent initialIsOpen position="bottom-right" />
                        </Hydrate>
                    </UniversalModalProvider>
                </QueryClientProvider>
            </ComponentUIDProvider>
        </React.Fragment>
    );
}
/**
 * This covers basic auth for roles and required permission roles within the Account Management Page.
 * For courses this will need be extended. More than likely an (Authoirizer) type middleware that can be passed via page by page basis
 * to determine the role, permissions and / or if that user even has the course as part of their enrollments.it
 *
 * @param param0
 * @returns
 */
function ClientAuth({
    children,
    requiredPermission,
    requiredRole,
}: {
    children: React.ReactNode;
    requiredPermission?: Permissions;
    requiredRole?: Roles;
}): any {
    const [session, loading, error] = useUserSession();
    const router = useRouter();
    const { hasPermission, hasRole } = useUserPermissions();
    const isUser = !!session;

    useEffect(() => {
        if (error && !loading && !session) {
            router.replace('/auth/signin');
        }
    }, [error, loading, session, router]);

    if (isUser) {
        if (!!requiredPermission) {
            if (!hasPermission(requiredPermission)) {
                router.replace('/');
                return null;
            }
        }

        if (!!requiredRole) {
            if (!hasRole(requiredRole)) {
                router.replace('/');
                return null;
            }
        }

        return children;
    }

    return <LottieLoaderFullscreen />;
}

export default MyApp;
